export type TutorialSide = 'top' | 'right' | 'bottom' | 'left';
export type TutorialAlign = 'start' | 'center' | 'end';

export interface PageTutorialStep {
  selector?: string;
  zoneKey?: string;
  title: string;
  description: string;
  side?: TutorialSide;
  align?: TutorialAlign;
}

export interface PageTutorialDefinition {
  key: string;
  version: number;
  label: string;
  steps: PageTutorialStep[];
}

const VERSION = 1;

const layoutStep = (layout: 'admin' | 'cashier' | 'base'): PageTutorialStep => {
  if (layout === 'cashier') {
    return {
      selector: '[data-tour="cashier-navbar"]',
      zoneKey: 'layout_cashier_navbar',
      title: 'Navigation caisse',
      description: 'Accédez vite à la caisse, aux inventaires et à la fin de journée. Conseil: gardez toujours le bon site actif avant de commencer une session.',
      side: 'bottom',
    };
  }

  if (layout === 'base') {
    return {
      selector: '[data-tour="base-navbar"]',
      zoneKey: 'layout_base_navbar',
      title: 'Espace sites',
      description: 'Cette barre sert à gérer vos sites et à revenir vers le site courant. Conseil: vérifiez le site actif avant de modifier ses paramètres.',
      side: 'bottom',
    };
  }

  return {
    selector: '[data-tour="admin-navbar"]',
    zoneKey: 'layout_admin_navbar',
    title: 'Navigation backoffice',
    description: 'Les modules sont regroupés par métier: achats, stock, ventes, rapports, utilisateurs et paramètres. Conseil: limitez les droits selon les responsabilités de chaque utilisateur.',
    side: 'bottom',
  };
};

const sharedSteps = (layout: 'admin' | 'cashier' | 'base'): PageTutorialStep[] => [
  layoutStep(layout),
  {
    selector: '[data-tour="page-content"]',
    zoneKey: 'layout_page_content',
    title: 'Zone de travail',
    description: 'C’est ici que les cartes, tableaux, formulaires et actions de la page apparaissent. Conseil: traitez les informations de haut en bas pour éviter les oublis.',
    side: 'top',
  },
  {
    selector: '[data-tour="datatable"]',
    zoneKey: 'component_datatable',
    title: 'Tableau de données',
    description: 'Utilisez la recherche, l’actualisation, l’impression et l’export quand ils sont disponibles. Conseil: vérifiez les filtres avant d’analyser une liste.',
    side: 'top',
  },
  {
    selector: '[data-tour="page-tutorial-button"]',
    zoneKey: 'component_page_tutorial_button',
    title: 'Revoir ce guide',
    description: 'Ce bouton relance le tutoriel de la page à tout moment, même si vous l’avez déjà terminé.',
    side: 'left',
  },
];

const makeTutorial = (
  routeName: string,
  label: string,
  description: string,
  advice: string,
  layout: 'admin' | 'cashier' | 'base' = 'admin',
  extraSteps: PageTutorialStep[] = []
): PageTutorialDefinition => ({
  key: routeName,
  version: VERSION,
  label,
  steps: [
    {
      title: label,
      description,
    },
    ...sharedSteps(layout),
    ...extraSteps,
    {
      title: 'Conseil de gestion',
      description: advice,
    },
  ],
});

export const PAGE_TUTORIALS: Record<string, PageTutorialDefinition> = {
  Dashboard: makeTutorial(
    'Dashboard',
    'Tableau de bord',
    'Cette page donne une vue rapide des ventes, stocks, achats, opérations et performances utilisateurs sur la période choisie.',
    'Surveillez surtout les ruptures, les marges et les performances caissiers: ce sont les signaux les plus rapides pour décider quoi corriger.'
  ),
  Purchases: makeTutorial(
    'Purchases',
    'Commandes d’achats',
    'Suivez les commandes fournisseurs, leurs lignes, validations et règlements.',
    'Gardez les brouillons propres: validez seulement quand les quantités, lots, dates d’expiration et prix fournisseur sont vérifiés.'
  ),
  PurchaseShow: makeTutorial(
    'PurchaseShow',
    'Détail d’achat',
    'Cette fiche permet de compléter les lignes, importer des produits, valider la commande et suivre les paiements fournisseur.',
    'Avant validation, contrôlez les lots et les écarts de prix fournisseur pour garder votre stock et vos coûts cohérents.'
  ),
  SupplierReturns: makeTutorial(
    'SupplierReturns',
    'Retours fournisseur',
    'Cette page sert à préparer et suivre les sorties de stock retournées aux fournisseurs.',
    'Choisissez toujours un motif de mouvement cohérent pour distinguer un vrai retour fournisseur d’une autre sortie.'
  ),
  SupplierReturnShow: makeTutorial(
    'SupplierReturnShow',
    'Détail retour fournisseur',
    'Contrôlez les produits retournés, les quantités et le statut de validation.',
    'Validez le retour uniquement quand les produits sont physiquement sortis du stock.'
  ),
  Suppliers: makeTutorial(
    'Suppliers',
    'Fournisseurs',
    'Gérez les fournisseurs, leurs coordonnées et les produits qu’ils peuvent livrer.',
    'Renseignez les délais et prix d’achat par produit pour fiabiliser les commandes.'
  ),
  SupplierShow: makeTutorial(
    'SupplierShow',
    'Détail fournisseur',
    'Consultez les informations du fournisseur, son historique d’achats validés et ses règlements.',
    'Surveillez le reste dû pour éviter les dettes fournisseur oubliées.'
  ),
  ProductDatabase: makeTutorial(
    'ProductDatabase',
    'Base de donnée produits',
    'Cette base regroupe tous les produits connus par l’application, actifs ou non dans le site courant.',
    'Activez seulement les produits utiles au site afin de garder les opérations de caisse et de stock rapides.'
  ),
  Products: makeTutorial(
    'Products',
    'Produits du site',
    'Cette liste montre les produits actifs du site courant, utilisés dans les ventes, achats, inventaires et mouvements.',
    'Gardez les codes-barres, rayons, taxes et prix à jour pour accélérer la caisse et améliorer les rapports.'
  ),
  ProductShow: makeTutorial(
    'ProductShow',
    'Détail produit',
    'Cette fiche centralise photos, fournisseurs, mouvements, historiques de ventes et d’achats du produit.',
    'Vérifiez régulièrement le fournisseur par défaut, le cumul stocké et les promotions pour éviter les écarts de marge.'
  ),
  ProductPromotions: makeTutorial(
    'ProductPromotions',
    'Promotions',
    'Planifiez les prix promotionnels et suivez les promotions créées depuis la caisse.',
    'Une promotion doit avoir une période claire pour éviter qu’une baisse temporaire devienne permanente.'
  ),
  StockMovements: makeTutorial(
    'StockMovements',
    'Mouvements de stock',
    'Consultez les entrées, sorties et ajustements avec leurs motifs.',
    'Un mouvement bien motivé facilite l’audit du stock et l’explication des écarts.'
  ),
  StockTransfers: makeTutorial(
    'StockTransfers',
    'Transferts de stock',
    'Préparez les transferts entre sites, envoyez les produits et suivez leur réception.',
    'Ne recevez un transfert que lorsque les quantités physiques sont confirmées.'
  ),
  Inventories: makeTutorial(
    'Inventories',
    'Inventaires',
    'Créez les brouillons, suivez le comptage, clôturez puis validez les inventaires.',
    'Clôturer bloque le comptage; valider applique les quantités au stock. Séparez bien ces deux étapes.'
  ),
  InventoryShow: makeTutorial(
    'InventoryShow',
    'Détail inventaire',
    'Analysez les lignes comptées, les écarts et les actions disponibles selon le statut.',
    'Validez seulement quand toutes les lignes ont été contrôlées.'
  ),
  Clients: makeTutorial(
    'Clients',
    'Clients',
    'Gérez les clients, leurs dettes, plafonds et règlements.',
    'Ne vendez à crédit qu’aux clients identifiés et dans la limite du crédit disponible.'
  ),
  ClientShow: makeTutorial(
    'ClientShow',
    'Détail client',
    'Consultez l’historique, la dette, les règlements et les ventes du client.',
    'Suivez les règlements partiels pour garder le crédit disponible exact.'
  ),
  Sales: makeTutorial(
    'Sales',
    'Historique ventes',
    'Retrouvez les ventes validées, annulées ou abandonnées, avec leurs statuts et moyens de paiement.',
    'Les ventes abandonnées restent consultables mais ne doivent pas servir aux analyses de performance.'
  ),
  SaleShow: makeTutorial(
    'SaleShow',
    'Détail vente',
    'Cette page présente les informations complètes de la vente, ses lignes, taxes, paiements et ticket.',
    'Contrôlez les lignes avant toute annulation: une vente crédit réglée ne doit pas être annulée sans traiter les règlements.'
  ),
  CashRegisters: makeTutorial(
    'CashRegisters',
    'Caisses',
    'Gérez les caisses, leurs sessions ouvertes et l’historique de service.',
    'Une caissière ne doit avoir qu’une session ouverte afin de garder le billetage fiable.'
  ),
  CashRegisterShow: makeTutorial(
    'CashRegisterShow',
    'Détail caisse',
    'Consultez les informations de la caisse et l’historique de ses sessions.',
    'Comparez le fond initial, l’ouverture et la clôture pour repérer les anomalies.'
  ),
  CashCounts: makeTutorial(
    'CashCounts',
    'Billetages',
    'Consultez les fins de journée créées par les caissiers et validez les contrôles.',
    'L’administrateur corrige et valide; la création doit rester côté caissier.'
  ),
  CashCountShow: makeTutorial(
    'CashCountShow',
    'Détail billetage',
    'Analysez le montant théorique, le comptage réel et les écarts de caisse.',
    'Un commentaire clair aide à expliquer les différences lors du contrôle.'
  ),
  DeliveriesView: makeTutorial(
    'DeliveriesView',
    'Livraisons',
    'Suivez les livraisons liées aux ventes et leur progression.',
    'Gardez les statuts de livraison à jour pour éviter les commandes oubliées.'
  ),
  DeliveryShow: makeTutorial(
    'DeliveryShow',
    'Détail livraison',
    'Consultez les coordonnées, le statut et le bon de livraison.',
    'Vérifiez le contact client avant chaque départ de livraison.'
  ),
  Rays: makeTutorial(
    'Rays',
    'Rayons',
    'Organisez les produits par rayon et paramétrez leurs couleurs.',
    'Des rayons bien colorés accélèrent le repérage en caisse et en inventaire.'
  ),
  RayShow: makeTutorial(
    'RayShow',
    'Détail rayon',
    'Consultez les informations du rayon et la liste de ses produits.',
    'Surveillez les produits sans rayon pour garder les filtres utiles.'
  ),
  Categories: makeTutorial(
    'Categories',
    'Catégories',
    'Classez les produits dans des catégories utiles aux analyses.',
    'Utilisez les catégories pour comparer les familles de produits sans multiplier les rayons.'
  ),
  CategoryShow: makeTutorial(
    'CategoryShow',
    'Détail catégorie',
    'Consultez la catégorie et les produits associés.',
    'Une catégorie claire rend les rapports produits plus exploitables.'
  ),
  Expenses: makeTutorial(
    'Expenses',
    'Liste des dépenses',
    'Retrouvez les dépenses du site avec leur type, bénéficiaire, statut et solde.',
    'Le bouton Planifications permet de modifier, mettre en pause ou reprendre les répétitions préparées depuis le formulaire de dépense.',
    'admin',
    [{ selector: '[data-tour="expense-filters"]', zoneKey: 'expense_filters', title: 'Filtres dépenses', description: 'Filtrez par dates, type, fournisseur ou statut.', side: 'bottom' }]
  ),
  ExpenseShow: makeTutorial(
    'ExpenseShow',
    'Détail de la dépense',
    'Modifiez un brouillon dans le dialogue, validez la dépense, enregistrez ses paiements et consultez ses justificatifs.',
    'Consultez les écritures dans le journal comptable des rapports et les changements dans l’activité récente.',
    'admin',
    [{ selector: '[data-tour="expense-payments"]', zoneKey: 'expense_payments', title: 'Paiements', description: 'L’historique reste append-only: les contre-paiements apparaissent sans supprimer les lignes d’origine.', side: 'top' }]
  ),
  ExpenseTypes: makeTutorial(
    'ExpenseTypes',
    'Types de dépenses',
    'Organisez les dépenses et reliez chaque type au compte de charge approprié.',
    'Vérifiez le compte comptable avant d’utiliser un nouveau type dans une dépense validée.'
  ),
  SalesReport: makeTutorial(
    'SalesReport',
    'Rapport ventes',
    'Analysez les ventes sur la période sélectionnée.',
    'Réinitialisez les filtres pour revenir aux 30 derniers jours avant de comparer les tendances.'
  ),
  PurchasesReport: makeTutorial(
    'PurchasesReport',
    'Rapport achats',
    'Suivez les achats validés et les montants fournisseur.',
    'Comparez les achats aux ventes pour anticiper les ruptures et surstocks.'
  ),
  ExpensesReport: makeTutorial(
    'ExpensesReport',
    'Rapport dépenses',
    'Analysez les charges par période, type, statut et moyen de paiement, puis exportez toutes les pages.',
    'Contrôlez le total payé et le reste dû avant de transmettre le rapport.',
    'admin',
    [{ selector: '[data-tour="expense-report-filters"]', zoneKey: 'expense_report_filters', title: 'Filtres du rapport', description: 'Sélectionnez la période, le statut et les types de dépenses à analyser.', side: 'bottom' }]
  ),
  InventoriesReport: makeTutorial(
    'InventoriesReport',
    'Rapport inventaires',
    'Analysez les inventaires et leurs écarts.',
    'Des écarts répétés sur un rayon signalent souvent un problème de procédure ou de rangement.'
  ),
  ProductsReport: makeTutorial(
    'ProductsReport',
    'Rapport produits',
    'Analysez la performance et l’état des produits.',
    'Croisez stock, marge et ventes pour prioriser les réapprovisionnements.'
  ),
  SupplierReturnsReport: makeTutorial(
    'SupplierReturnsReport',
    'Rapport retours fournisseur',
    'Analysez les retours vers fournisseurs.',
    'Un volume élevé de retours peut signaler un problème de qualité ou de réception.'
  ),
  SupplierPaymentsReport: makeTutorial(
    'SupplierPaymentsReport',
    'Rapport paiements fournisseur',
    'Suivez les paiements effectués aux fournisseurs.',
    'Gardez une lecture régulière des dettes pour éviter les retards.'
  ),
  StockMovementsReport: makeTutorial(
    'StockMovementsReport',
    'Rapport mouvements',
    'Analysez les mouvements de stock par période et motif.',
    'Les sorties manuelles doivent rester exceptionnelles et documentées.'
  ),
  StockTransfersReport: makeTutorial(
    'StockTransfersReport',
    'Rapport transferts',
    'Analysez les transferts entre sites.',
    'Les transferts fréquents peuvent révéler un mauvais équilibrage du stock entre sites.'
  ),
  CashRegistersReport: makeTutorial(
    'CashRegistersReport',
    'Rapport sessions de caisse',
    'Analysez les horaires et les sessions des caissiers.',
    'Comparez les heures travaillées aux ventes pour évaluer la performance opérationnelle.'
  ),
  DeliveriesReport: makeTutorial(
    'DeliveriesReport',
    'Rapport livraisons',
    'Analysez les livraisons sur la période.',
    'Surveillez les retards pour améliorer la satisfaction client.'
  ),
  AccountingJournalReport: makeTutorial(
    'AccountingJournalReport',
    'Journal comptable',
    'Consultez les pièces comptables enregistrées avec leurs totaux débit et crédit.',
    'Une écriture doit toujours rester équilibrée avant d’être utilisée pour l’audit.'
  ),
  AccountingLedgerReport: makeTutorial(
    'AccountingLedgerReport',
    'Grand livre comptable',
    'Analysez le détail des mouvements par compte comptable.',
    'Filtrez par période et compte avant de contrôler un solde.'
  ),
  AccountingBalanceReport: makeTutorial(
    'AccountingBalanceReport',
    'Balance comptable',
    'Consultez les totaux débit, crédit et solde par compte.',
    'La balance sert de premier contrôle avant tout rapprochement comptable.'
  ),
  AccountingExceptionsReport: makeTutorial(
    'AccountingExceptionsReport',
    'Anomalies comptables',
    'Suivez les erreurs ou comptes manquants à corriger avant les écritures automatiques.',
    'Traitez les anomalies ouvertes avant de lancer une période comptable fiable.'
  ),
  Users: makeTutorial(
    'Users',
    'Utilisateurs',
    'Créez les comptes, rôles, permissions et affectations temporaires.',
    'Donnez le minimum de droits nécessaire et vérifiez le site affecté.'
  ),
  UserShow: makeTutorial(
    'UserShow',
    'Détail utilisateur',
    'Consultez le profil, les droits et les actions de l’utilisateur.',
    'Révoquez les affectations temporaires dès qu’elles ne sont plus nécessaires.'
  ),
  ActivityLogs: makeTutorial(
    'ActivityLogs',
    'Activités récentes',
    'Consultez les modifications enregistrées dans l’application. Le filtre Module est réservé au superadmin et au support.',
    'Utilisez ce journal pour comprendre qui a créé, modifié ou supprimé une donnée; pour un audit par module, demandez au superadmin ou au support.'
  ),
  SettingsGeneral: makeTutorial(
    'SettingsGeneral',
    'Paramètres généraux',
    'Réglez les préférences utilisateur générales.',
    'Les préférences utilisateur modifient l’ergonomie sans changer les données métier.'
  ),
  SettingsPrinting: makeTutorial(
    'SettingsPrinting',
    'Paramètres d’impression',
    'Configurez l’impression thermique, le pont local et les tests de caisse.',
    'L’URL et le token du pont doivent rester locaux au poste.'
  ),
  SettingsInventory: makeTutorial(
    'SettingsInventory',
    'Paramètres inventaire',
    'Réglez le comportement du comptage inventaire.',
    'Masquer les produits traités accélère le comptage, mais afficher tout aide lors des contrôles.'
  ),
  SettingsAccounting: makeTutorial(
    'SettingsAccounting',
    'Paramètres comptables',
    'Associez les rôles comptables du site aux comptes imputables du plan SYSCOHADA révisé.',
    'Complétez tous les comptes requis avant d’activer les écritures automatiques.'
  ),
  ShopInfo: makeTutorial(
    'ShopInfo',
    'Informations boutique',
    'Configurez les informations, devises, taxes et paramètres du site.',
    'Les taux de TVA/CSS influencent l’extraction fiscale des ventes.'
  ),
  DeliverySettings: makeTutorial(
    'DeliverySettings',
    'Réglages livraisons',
    'Configurez les paramètres utilisés par les livraisons.',
    'Des règles claires réduisent les erreurs de frais et de suivi.'
  ),
  MovementSettings: makeTutorial(
    'MovementSettings',
    'Réglages mouvements',
    'Gérez les motifs standards de mouvements de stock.',
    'Les motifs système ne sont pas modifiables car ils expliquent les flux automatiques.'
  ),
  SiteShow: makeTutorial(
    'SiteShow',
    'Détail site',
    'Consultez et ajustez les informations d’un site.',
    'Le site principal porte les éléments globaux comme le logo principal.'
  ),
  Sites: makeTutorial(
    'Sites',
    'Mes sites',
    'Gérez les sites accessibles et choisissez le site opérationnel.',
    'Avant de travailler, vérifiez que vous êtes sur le bon site pour éviter les erreurs de stock.',
    'base'
  ),
  CashierSales: makeTutorial(
    'CashierSales',
    'Caisse',
    'Cette interface sert à rechercher les produits, gérer le panier, choisir le client et enregistrer la vente.',
    'Vérifiez le client avant une vente à crédit et surveillez toujours le crédit disponible.',
    'cashier',
    [
      {
        selector: '[data-tour="cashier-online"]',
        zoneKey: 'cashier_connection_status',
        title: 'État de connexion',
        description: 'Le crédit et certaines validations demandent d’être en ligne pour éviter les dépassements ou doublons.',
        side: 'bottom',
      },
    ]
  ),
  CashierInventories: makeTutorial(
    'CashierInventories',
    'Inventaires caissier',
    'Choisissez un inventaire brouillon à compter.',
    'Le caissier ne crée pas l’inventaire: il compte uniquement les brouillons préparés.',
    'cashier'
  ),
  CashierInventoryCount: makeTutorial(
    'CashierInventoryCount',
    'Comptage inventaire',
    'Saisissez les quantités réelles, validez les cartes et avancez produit par produit.',
    'Une quantité vide signifie non comptée; validez seulement après vérification physique.',
    'cashier'
  ),
  CashierEndOfDay: makeTutorial(
    'CashierEndOfDay',
    'Fin de journée',
    'Préparez le billetage et clôturez la journée de caisse.',
    'Le montant théorique aide à comparer les espèces attendues au comptage réel.',
    'cashier',
    [
      {
        selector: '[data-tour="cashier-end-day-cash-totals"]',
        zoneKey: 'cashier_end_day_cash_totals',
        title: 'Totaux d’espèces',
        description: 'Comparez le fond initial, les espèces encaissées, le flux net des transactions portefeuille et le montant théorique.',
        side: 'top',
      },
      {
        selector: '[data-tour="cashier-end-day-status"]',
        zoneKey: 'cashier_end_day_status',
        title: 'Statut de la journée',
        description: 'Ce badge indique immédiatement si la journée est encore ouverte, en cours de contrôle ou clôturée.',
        side: 'bottom',
      },
    ]
  ),
  StockCalendar: makeTutorial(
    'StockCalendar',
    'Calendrier des opérations',
    'Visualisez par semaine les ventes, achats, mouvements de stock et expirations du site connecté.',
    'Utilisez les couleurs pour identifier le type d’opération, puis cliquez sur un événement pour ouvrir son détail.',
    'admin',
    [
      {
        selector: '[data-tour="stock-calendar-legend"]',
        zoneKey: 'stock_calendar_legend',
        title: 'Légende des événements',
        description: 'Chaque couleur correspond à une famille d’événements. Vérifiez la légende avant d’interpréter la semaine.',
        side: 'bottom',
      },
      {
        selector: '[data-tour="stock-calendar-grid"]',
        zoneKey: 'stock_calendar_grid',
        title: 'Semaine visible',
        description: 'Naviguez entre les semaines et sélectionnez un événement pour consulter sa référence, son auteur, ses montants ou ses informations de lot.',
        side: 'top',
      },
    ]
  ),
  ElectronicWallets: makeTutorial(
    'ElectronicWallets',
    'Portefeuilles électroniques',
    'Consultez les soldes et l’historique des recharges, dépôts, retraits, ajustements et annulations.',
    'Un solde ne doit jamais être modifié directement : utilisez une opération ou un ajustement motivé pour conserver la traçabilité.',
    'admin',
    [
      {
        selector: '[data-tour="wallet-cards"]',
        zoneKey: 'wallet_balance_cards',
        title: 'Soldes des portefeuilles',
        description: 'Chaque carte présente le solde actuel, le code et le volume d’opérations du portefeuille.',
        side: 'bottom',
      },
      {
        selector: '[data-tour="wallet-filters"]',
        zoneKey: 'wallet_transaction_filters',
        title: 'Filtres des opérations',
        description: 'Filtrez l’historique par portefeuille, type, statut ou période avant de rechercher une référence.',
        side: 'bottom',
      },
    ]
  ),
  WalletsReport: makeTutorial(
    'WalletsReport',
    'Rapport des portefeuilles',
    'Analysez les opérations électroniques et leur impact sur les espèces pendant la période choisie.',
    'Vérifiez que les filtres du tableau correspondent aux cartes de synthèse avant d’exporter les résultats.',
    'admin',
    [
      {
        selector: '[data-tour="wallet-filters"]',
        zoneKey: 'wallet_report_filters',
        title: 'Périmètre du rapport',
        description: 'Choisissez la période, le portefeuille, l’opération et le statut à analyser.',
        side: 'bottom',
      },
      {
        selector: '[data-tour="wallet-report-summary"]',
        zoneKey: 'wallet_report_summary',
        title: 'Synthèse portefeuille',
        description: 'Ces cartes résument les ventes, dépôts, retraits, l’impact espèces et le nombre d’écritures correspondant aux filtres.',
        side: 'bottom',
      },
    ]
  ),
  RevenueMarginsReport: makeTutorial(
    'RevenueMarginsReport',
    'Chiffre d’affaires et marges',
    'Mesurez le chiffre d’affaires, le coût estimé et la contribution de chaque produit à la marge.',
    'Comparez toujours la marge au chiffre d’affaires et contrôlez les produits dont le taux est faible ou négatif.',
    'admin',
    [
      {
        selector: '[data-tour="revenue-margin-filters"]',
        zoneKey: 'revenue_margin_filters',
        title: 'Période analysée',
        description: 'Sélectionnez les dates de début et de fin, puis appliquez le filtre pour recalculer toutes les valeurs.',
        side: 'bottom',
      },
      {
        selector: '[data-tour="revenue-margin-summary"]',
        zoneKey: 'revenue_margin_summary',
        title: 'Indicateurs de rentabilité',
        description: 'Les cartes rapprochent chiffre d’affaires, coût estimé, marge brute, taux de marge et nombre de produits vendus.',
        side: 'bottom',
      },
    ]
  ),
  SalesDaySummary: makeTutorial(
    'SalesDaySummary',
    'Journées de caisse',
    'Contrôlez les sessions, ventes, billetages et transactions portefeuille d’une journée donnée.',
    'Filtrez d’abord la date et le caissier, puis rapprochez les produits vendus, les flux portefeuille et le comptage de caisse.',
    'admin',
    [
      {
        selector: '[data-tour="day-summary-filters"]',
        zoneKey: 'day_summary_filters',
        title: 'Date et caissier',
        description: 'Choisissez une journée et, si nécessaire, un caissier précis avant de lancer le contrôle.',
        side: 'bottom',
      },
      {
        selector: '[data-tour="day-summary-totals"]',
        zoneKey: 'day_summary_totals',
        title: 'Totaux de la journée',
        description: 'Ces cartes regroupent ventes, moyens de paiement, fonds initiaux, montant théorique et écarts de billetage.',
        side: 'bottom',
      },
      {
        selector: '[data-tour="day-summary-sessions"]',
        zoneKey: 'day_summary_sessions',
        title: 'Sessions de caisse',
        description: 'Consultez les heures d’ouverture et de clôture, le statut du billetage et les éventuelles actions de contrôle.',
        side: 'top',
      },
    ]
  ),
};

export const getPageTutorial = (routeName: unknown): PageTutorialDefinition | null => {
  const name = String(routeName || '');
  return PAGE_TUTORIALS[name] || null;
};
